
import { Box, Button, IconButton, Stack } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from 'ag-grid-react';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { AG_GRID_LOCALE_JP } from '../../themes/localeJP';
import LoadingOverlay, { DefaultLoadingMessage } from '../common/LoadingOverlay';
import NoRowsOverlay, { NoRowsMessage } from '../common/NoRowsOverlay';

import "../common/AgGridReactCustom.css"
import { PageData } from "../../models/Index";
import PaginationCpn from "../common/PaginationCpn";
import "./TestGrid.css"
import { CONTENT_COLOR } from "../../models/Constants";
import DeleteIcon from '@mui/icons-material/Delete';

// -------------
// Props
export type UserGridProps = {
    pageSize?: number;
    pageData?: PageData;
    keyword: string;
    onSelected: (data: any) => void;
    onDoubleClick: (data: any) => void;
    onDeleteScore: (data: any) => void;
    onChangeSearchCondition: (name: string, value: any) => void;
    isSearch:boolean
};

// -------------
// Component
export const TestGrid = forwardRef((props: UserGridProps, ref) => {
    const {
        pageSize,
        pageData,
        keyword,
        onSelected,
        onDoubleClick,
        onDeleteScore,
        onChangeSearchCondition,
        isSearch
    } = props;
    const gridRef = useRef() as any;
    const [rowData, setRowData] = useState<any[]>([]);
    const [event, setEvent] = useState<any>();
    useImperativeHandle(ref, () => ({
        setRowData(value: any) {
            setRowData(value);
        },

        hideOverlay() {
            gridRef.current.api.hideOverlay();
        }
    }));
    useEffect(() =>{
        if(isSearch && event != undefined){
            event.node.setSelected(!event.node.isSelected());
        }
    },[isSearch])
    // Loading時のメッセージ
    const loadingOverlayComponent = useMemo(() => {
        return LoadingOverlay;
    }, []);
    
    const loadingOverlayComponentParams = useMemo(() => {
        return {
            loadingMessage: DefaultLoadingMessage,
        };
    }, []);

    const noRowsOverlayComponent = useMemo(() => {
        return NoRowsOverlay;
    }, []);

    const noRowsOverlayComponentParams = useMemo(() => {
        return {
            noRowsMessageFunc: () => NoRowsMessage,
        };
    }, []);

    // 全列に共通の定義
    const defaultColDef = {
        sortable: true, // ソート可
        editable: false, // 編集不可
        resizable: true,
        suppressMovable: true,  // 列の移動を無効にする
    };

    // 列タイプの定義
    const columnTypes = {
        nonEditableColumn: { editable: false }, // 編集不可
    };

    const handleChecked =(e:any) =>{
        onSelected(e.data)
        setEvent(e)
    }

    const handleRowDoubleClick = (e:any) => {
        onSelected(e.data)
        onDoubleClick(e.data)
    }

    const CellTypeRenderer = (props:any) =>{
        switch(Number(props.data.typeResult))
        {
            case 1:
                return '1：積極協働型';
            case 2:
                return '2：積極協働型';
            case 3:
                return '3：平均型';
            case 4:
                return '4：協調融和型';
            case 5:
                return '5：要開発型';
            default:
                return '該当なし';
        }
    }

    const CellViewingRenderer = (props: any) => {
        return (
            <Box  justifySelf={"center"}>
                <Button variant="contained" sx={{height:"30px"}} onClick={()=>handleRowDoubleClick(props)}>診断表</Button>
            </Box>
        );
    };

    const handleOpenDelete = (e:any) => {
        onSelected(e.data)
        onDeleteScore(e.data)
    }

    const CellControlRenderer = (props: any) => {
        return (
            <Stack direction={"row"} justifyContent={"center"}>
                    <IconButton onClick={()=>handleOpenDelete(props)}><DeleteIcon sx={{color:CONTENT_COLOR}}/></IconButton>
            </Stack>
        );
    };

    // 列の個別定義
    const columnDefs = [
        {
            headerName: "氏名",
            field: "userName",
            rowDrag: false,
            flex: 3,
        },
        {
            headerName: "実施日",
            field: "testDate",
            rowDrag: false,
            flex: 3,
        },
        {
            headerName: '検査結果の信頼性',
            children: [
                { headerName: '信用尺度', field: "type10Score", flex: 3, rowDrag: false },
                { headerName: '疑問点', field: "undefineTypeScore", flex: 3, rowDrag: false  }
            ],
            rowDrag: false,
        },
        {
            headerName: "原因別ストレス耐性",
            field: 'stressToleranceTypeScore',
            rowDrag: false,
            flex: 3,
        },
        {
            headerName: 'ストレス対処資質',
            children: [
                { headerName: '総合判定', field: "copingAbilityTypeScore", flex: 3, rowDrag: false },
                { headerName: 'タイプ', field: "typeResult", flex: 3, rowDrag: false, cellRenderer: CellTypeRenderer }
            ],
            rowDrag: false,
        },
        {
            headerName: "",
            field:"",
            rowDrag: false,
            flex: 2,
            cellRenderer: CellViewingRenderer,
        },
        {
            headerName: "",
            field: "",
            rowDrag: false,
            flex: 2,
            cellRenderer: CellControlRenderer,
        }
    ];
    const rowHeight = 48;
    gridRef.current?.api?.setQuickFilter(keyword);
    return (
        <>
            <div>
                <div className="ag-theme-alpine custom-grid" style={{ height: 'calc(100vh - 250px)', width: "100%"}}>
                    <AgGridReact
                        ref={gridRef}
                        defaultColDef={defaultColDef}
                        columnTypes={columnTypes}
                        columnDefs={columnDefs}
                        domLayout={"normal"}
                        rowData={rowData}
                        animateRows={true}
                        rowDragManaged={true}
                        suppressMovableColumns={false}
                        suppressDragLeaveHidesColumns={false}
                        rowHeight={rowHeight}
                        localeText={AG_GRID_LOCALE_JP}
                        loadingOverlayComponent={loadingOverlayComponent}
                        loadingOverlayComponentParams={loadingOverlayComponentParams}
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                        suppressRowHoverHighlight={true}
                        rowSelection={"single"}
                        onCellClicked={handleChecked}
                        onRowDoubleClicked={handleRowDoubleClick}
                    />
                </div>
                {pageSize && pageData && <PaginationCpn  pageData={pageData} onChangeSearchCondition={onChangeSearchCondition} limit={pageSize} />}
            </div>
        </>
    );
});

export default TestGrid;